import React, { useEffect, useRef, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Button, CircularProgress, Flex, Image, Text } from '@chakra-ui/react';
import { Auth } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { signOutOfAuthSession } from 'clipsal-cortex-utils/src/auth/cached-session';
import { IS_NATIVE } from 'clipsal-cortex-utils/src/constants/common-constants';

import loggedOutImg from '../../../assets/images/logged_out.svg';
import { COMPONENT_MIN_HEIGHT, IS_RUNNING_TESTS } from '../../../common/constants';
import { selectUser } from '../../user/userSlice';
import { getCognitoLogOutUrl, getIdpLogOutUrl } from '../auth-helpers';
import styles from './Logout.module.scss';

export function Logout() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const [isLoaded, setLoaded] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const userIdAtLogoutStart = useRef(user.userID);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    /**
     * Log out of the application.
     * This is done in 3 steps:
     *  1. Log out of the EIM IdP session
     *  2. Log out of the Cognito session
     *  3. Clear out everything else (reset Redux store and call `await Auth.signOut()`)
     */
    async function logOut() {
      if (IS_RUNNING_TESTS) {
        setLoaded(true);
        return;
      }

      // Step 1: Resolve the IDP logout URL while the Amplify session is still active.
      // getIdpLogOutUrl() calls Auth.currentAuthenticatedUser() internally — it must
      // run before signOutOfAuthSession clears the Amplify session.
      let idpLogOutUrl: string | null = null;
      try {
        idpLogOutUrl = await getIdpLogOutUrl();
      } catch (error) {
        console.error('Error getting IDP log out URL', error);
      }

      // Step 2 (web only): store the EIM logout URL in sessionStorage BEFORE calling
      // signOutOfAuthSession. Amplify v4 with OAuth uses urlOpener for signOut, which
      // calls Browser.open(_self) and navigates the page away immediately — any code
      // after signOutOfAuthSession is unreachable on web.
      // sessionStorage survives the redirect since it stays in the same browser tab.
      if (!IS_NATIVE && idpLogOutUrl) {
        sessionStorage.setItem('pendingIdpLogout', idpLogOutUrl);
      }

      // Step 3: Sign out of Amplify and clear the local token cache.
      // Storage.remove runs before Auth.signOut() inside signOutOfAuthSession so the
      // cache is always cleared even if signOut navigates away.
      // On web, Auth.signOut() also navigates to the Cognito logout URL (via urlOpener),
      // clearing the Cognito session cookie and redirecting back to the app.
      // On native, it only clears local tokens — browser sessions are handled in Step 4.
      try {
        await signOutOfAuthSession(Auth);
      } catch (error) {
        console.error('Error during sign out:', error);
      }

      // Step 4: Clean up local app state.
      // On web this may not execute (Auth.signOut navigated away), but that's fine —
      // the Redux store resets on page reload and localStorage is cleared on next mount.
      const userId = userIdAtLogoutStart.current;
      localStorage.removeItem(`${userId}_firstNameEIM`);
      localStorage.removeItem(`${userId}_lastNameEIM`);
      dispatch({ type: 'store/reset' });

      // Step 5 (native only): kill remote browser session via the in-app browser.
      // On web, Auth.signOut() already handled the Cognito logout redirect (Step 3),
      // and Login.tsx will handle the EIM logout via pendingIdpLogout (set in Step 2).
      if (IS_NATIVE) {
        try {
          // Open `url` in the in-app browser, close it on first page load, and resolve
          // only when `browserFinished` fires (i.e. the browser overlay is fully gone).
          const openAndCloseOnLoad = async (url: string): Promise<void> => {
            return new Promise<void>((resolve) => {
              let resolveStep: (() => void) | null = () => resolve();
              let pageLoadedListener: { remove: () => void } | null = null;
              let finishedListener: { remove: () => void } | null = null;

              // Safety timeout: if browser doesn't complete in 10 seconds, force resolve
              const timeoutId = setTimeout(() => {
                console.warn('Browser logout timeout on iOS, resolving anyway');
                pageLoadedListener?.remove();
                finishedListener?.remove();
                resolveStep?.();
                resolveStep = null;
              }, 10000);

              Browser.addListener('browserFinished', () => {
                clearTimeout(timeoutId);
                pageLoadedListener?.remove();
                finishedListener?.remove();
                if (resolveStep) {
                  resolveStep();
                  resolveStep = null;
                }
              }).then((listener) => {
                finishedListener = listener;
              });

              Browser.addListener('browserPageLoaded', async () => {
                if (pageLoadedListener) {
                  pageLoadedListener.remove();
                  pageLoadedListener = null;
                  await Browser.close().catch((error) => console.error('Error closing browser', error));
                }
              }).then((listener) => {
                pageLoadedListener = listener;
              });

              Browser.open({ url, windowName: '_self' }).catch((error) => {
                console.error('Error opening browser', error);
                clearTimeout(timeoutId);
                pageLoadedListener?.remove();
                finishedListener?.remove();
                if (resolveStep) {
                  resolveStep();
                  resolveStep = null;
                }
              });
            });
          };

          // Single-hop native remote logout to reduce visible open/close flashing.
          // Prefer IDP logout (main source of silent re-auth regression), and fall back
          // to Cognito logout if IDP URL could not be resolved.
          await openAndCloseOnLoad(idpLogOutUrl ?? getCognitoLogOutUrl());
        } catch (error) {
          console.error('Error during native browser logout', error);
        }
      } else {
        // Web: Auth.signOut() (Step 3) already navigated to Cognito logout via urlOpener,
        // clearing the Cognito session and redirecting back to the app. This branch is
        // unreachable in practice, but kept as an explicit fallback.
        window.location.href = getCognitoLogOutUrl();
      }

      setLoaded(true);
    }

    logOut();
    // Intentionally run once on mount. Including user.userID causes the logout flow
    // to re-run after store/reset updates user state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setTimeout(() => {
      setShouldRedirect(true);
    }, 7000);
  });

  useEffect(() => {
    if (isLoaded && shouldRedirect) navigate('/');
  }, [isLoaded, navigate, shouldRedirect]);

  return isLoaded ? (
    <>
      <div className={styles.container} id="logged-out-card">
        <div className={styles.logoutContainer}>
          <Image mb={4} w="60%" src={loggedOutImg} alt="Page not found" />
          <Text mb={2} fontSize="md">
            {t('Account.you have logged out')}
          </Text>
          <Button variant="outline">
            <Link to="/login">{t('Account.click here to log in')}</Link>
          </Button>
        </div>
      </div>
    </>
  ) : (
    <Flex direction="column" height={COMPONENT_MIN_HEIGHT} align="center" justify="center">
      <Text fontSize="lg" mb={2}>
        {t('Common.loading')}
      </Text>
      <CircularProgress isIndeterminate color="green" />
    </Flex>
  );
}
